import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

// Japanese theme assets (brush strokes only)
import BrushOne from '../Assets/BrushOne.png';
import BrushTwo from '../Assets/BrushTwo.png';
import BrushThree from '../Assets/BrushThree.png';
import BrushFour from '../Assets/BrushFour.png';

export default function AuditionForm({ uid, name }) {
  const [youtubeLink, setYoutubeLink] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  // Escucha en tiempo real los cambios en Firestore
  useEffect(() => {
    if (!uid) return;
    const reservationRef = doc(db, 'reservations', uid);
    const unsubscribe = onSnapshot(reservationRef, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      // Si hay un enlace guardado, marcamos que ya aplicó; si lo quitaron, volvemos a false
      setHasApplied(!!data.auditionLink);
    });
    return () => unsubscribe();
  }, [uid]);

  // Validación simple de enlace de YouTube
  const isValidYouTubeLink = (url) =>
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/.test(url);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidYouTubeLink(youtubeLink)) {
      setError('Por favor ingresa un enlace de YouTube válido.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await updateDoc(doc(db, 'reservations', uid), {
        auditionLink: youtubeLink,
        initialPayment: true,
      });
      // La actualización en tiempo real de onSnapshot actualizará hasApplied automáticamente
    } catch (err) {
      console.error('Error guardando enlace:', err);
      setError('Ocurrió un error al guardar. Intenta de nuevo.');
    }
    setLoading(false);
  };

  // Fondos decorativos de pinceladas
  function BrushBackgrounds() {
    const brushes = [
      { src: BrushOne, style: { top: '8%', left: '8%', width: '100px', rotate: '-12deg' } },
      { src: BrushTwo, style: { top: '50%', right: '10%', width: '140px', rotate: '18deg' } },
      { src: BrushThree, style: { bottom: '25%', left: '15%', width: '120px', rotate: '-6deg' } },
      { src: BrushFour, style: { bottom: '8%', right: '22%', width: '110px', rotate: '15deg' } },
    ];
    return (
      <div className="absolute inset-0 z-0 pointer-events-none">
        {brushes.map((b, i) => (
          <img
            key={i}
            src={b.src}
            alt="brush"
            className="absolute opacity-30"
            style={{ ...b.style, transform: `rotate(${b.style.rotate})` }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center px-4 py-16">
      <BrushBackgrounds />
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg p-10 lg:p-14 border border-red-500"
      >
        <h1 className="text-5xl font-serif font-bold text-red-600 text-center mb-4">
          Audición Simanof
        </h1>
        <p className="text-gray-700 text-center mb-8">
          Bienvenido a la Simanof Japan Tour 2026! Estamos emocionados de brindarte una experiencia musical inolvidable.
        </p>

        {/* Detalles del tour y requisitos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div>
            <h2 className="text-xl font-semibold text-red-600 mb-4">Detalles del Tour</h2>
            <ul className="space-y-3 text-gray-800">
              <li><strong>Fechas:</strong> 3–12 de agosto, 2026</li>
              <li><strong>Ciudades:</strong> Tokio, Osaka, Kioto</li>
              <li><strong>Período de audición:</strong> 1–25 de julio, 2026</li>
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-red-600 mb-4">Requisitos</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-800">
              <li>Un concierto de tu elección</li>
              <li>Escala mayor (cualquier tonalidad), 2 octavas</li>
              <li>Escala menor (cualquier tonalidad), 2 octavas</li>
            </ul>
          </div>
        </div>

        {hasApplied ? (
          <div className="bg-green-50 border border-green-300 p-6 rounded-xl shadow-inner text-center">
            <h2 className="text-2xl font-bold text-green-700 mb-4">¡Video enviado!</h2>
            <p className="text-gray-700 text-lg mb-2">
              Gracias por enviar tu audición. Tu participación ha sido registrada.
            </p>
            <p className="text-sm text-gray-600">
              Por favor realiza el pago de <strong>$75 USD</strong> vía PayPal a{' '}
              <strong>Simanofusa@gmail.com</strong> para completar tu inscripción.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <input
              type="text"
              placeholder="Enlace de YouTube..."
              value={youtubeLink}
              onChange={(e) => setYoutubeLink(e.target.value)}
              className="w-full p-5 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-200 shadow-inner"
            />
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-5 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl font-semibold uppercase hover:from-red-700 shadow-lg disabled:opacity-50"
            >
              {loading ? 'Procesando...' : 'Registrar Audición'}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
